<script lang="ts" setup>
import {computed, ref, watch} from 'vue'
import i18next from 'i18next'
import {useRouter} from 'vue-router'
import {storeToRefs} from 'pinia'
import {useAppStore} from '@/stores/app'
import {useToast} from '@/composables/useToast'
import TaskEditDialog from '@/components/TaskEditDialog.vue'
import {getDefaultTaskRoute} from '@/utils/defaultRoute'

const props = defineProps<{
  taskId: string
}>()

const router = useRouter()
const appStore = useAppStore()
const {allTasks} = storeToRefs(appStore)
const toast = useToast()

const dialogOpen = ref(false)
const isLoadingTask = ref(false)

const task = computed(() => {
  const id = parseInt(props.taskId)
  return allTasks.value.find((t) => t.task_id === id) ?? null
})

const taskInstance = computed(() => {
  if (!task.value) return null
  const undone = task.value.instances.find((i) => !i.done)
  return undone ?? task.value.instances[0] ?? null
})

async function redirectToTaskFolder() {
  const folder = task.value?.folder ?? 'inbox'
  await router.replace(`/${folder}`)
}

async function redirectToFallbackRoute() {
  await router.replace(getDefaultTaskRoute(allTasks.value))
}

async function openTaskFromDeepLink() {
  const taskId = Number.parseInt(props.taskId, 10)
  if (Number.isNaN(taskId)) {
    toast.error(i18next.t('messages.task_open_error'))
    await redirectToFallbackRoute()
    return
  }

  isLoadingTask.value = true

  try {
    const loadedTask = await appStore.loadTaskById(taskId)
    if (!loadedTask) {
      toast.error(i18next.t('messages.task_not_found'))
      await redirectToFallbackRoute()
      return
    }

    dialogOpen.value = true
  } catch (error) {
    console.error('Failed to open deep-linked task:', error)
    toast.error(i18next.t('messages.task_open_error'))
    await redirectToFallbackRoute()
  } finally {
    isLoadingTask.value = false
  }
}

watch(
  () => props.taskId,
  async () => {
    dialogOpen.value = false
    await openTaskFromDeepLink()
  },
  {immediate: true}
)

watch(dialogOpen, async (open) => {
  if (!open && task.value && !isLoadingTask.value) {
    await redirectToTaskFolder()
  }
})
</script>

<template>
  <div class="flex items-center justify-center h-full">
    <TaskEditDialog
      v-if="task && !isLoadingTask"
      :task="task"
      :task-instance="taskInstance"
      :open="dialogOpen"
      no-auto-focus
      @update:open="dialogOpen = $event" />
    <p v-else-if="isLoadingTask || allTasks.length === 0" class="text-muted-foreground">
      {{ $t('common.loading') }}
    </p>
  </div>
</template>
